import * as Sentry from '@sentry/vue';
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import type { Ref } from 'vue';
import type {
  EventResponse,
  CommitmentResponse,
  PurchaseResponse,
  RegistrationDiscoveryResponse,
  RegistrationAttendeeResponse,
} from '@@/types/api';
import { deriveViewerRole, type ViewerRole } from '~/utils/event-policy';
import { useEventsApi } from '~/composables/api/use-events-api';
import { useCommitmentsApi } from '~/composables/api/use-commitments-api';
import { usePurchasesApi } from '~/composables/api/use-purchases-api';
import { useRegistrationApi } from '~/composables/api/use-registration-api';
import { useAuthStore } from '~/stores/use-auth-store';

const REFRESH_INTERVAL_MS = 60_000;

interface UseEventEnrichmentOptions {
  eventId: Ref<string>;
  asyncData: Ref<EventResponse | null | undefined>;
  refreshPublicEvent: () => Promise<void>;
}

export function useEventEnrichment(options: UseEventEnrichmentOptions) {
  const { eventId, asyncData, refreshPublicEvent } = options;

  const viewerRole = ref<ViewerRole>('visitor');
  const commitment = ref<CommitmentResponse | null>(null);
  const purchase = ref<PurchaseResponse | null>(null);
  const registration = ref<RegistrationDiscoveryResponse | null>(null);
  const claimedAttendance = ref<RegistrationAttendeeResponse | null>(null);
  const isEnriching = ref(false);

  const authStore = useAuthStore();
  const eventsApi = useEventsApi();
  const commitmentsApi = useCommitmentsApi();
  const purchasesApi = usePurchasesApi();
  const registrationApi = useRegistrationApi();

  let refreshTimer: ReturnType<typeof setInterval> | null = null;
  let visibilityHandler: (() => void) | null = null;
  let paused = false;

  async function enrich() {
    const id = eventId.value;
    if (!id || !authStore.isAuthenticated) return;

    isEnriching.value = true;
    try {
      // Fetch authenticated event (required) alongside optional commitment/purchase
      // and registration discovery. Catch on the optional ones because the backend
      // may return 404 when none exist.
      const [
        enrichedEvent,
        commitmentResult,
        purchaseResult,
        registrationResult,
      ] = await Promise.all([
        eventsApi.getEventAuthenticated(id),
        commitmentsApi.getMyCommitment(id).catch(() => null),
        purchasesApi.getMyPurchase(id).catch(() => null),
        registrationApi.getMyRegistration(id).catch(() => null),
      ]);

      asyncData.value = enrichedEvent;

      commitment.value = commitmentResult;
      purchase.value = purchaseResult;
      registration.value = registrationResult;

      // If the user is a claimed attendee (someone else's invite), fetch
      // their attendance row so the UI can render the ClaimedAttendeeView.
      if (registrationResult?.is_claimed_attendee) {
        claimedAttendance.value = await registrationApi
          .getMyAttendance(id)
          .catch(() => null);
      } else {
        claimedAttendance.value = null;
      }

      viewerRole.value = deriveViewerRole({
        isAuthenticated: true,
        apiViewerRole: enrichedEvent.viewer_role,
        commitment: commitmentResult,
        purchase: purchaseResult,
      });
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      isEnriching.value = false;
    }
  }

  async function refreshAndEnrich() {
    try {
      await refreshPublicEvent();
    } catch {
      // Swallow — public refresh failure is non-critical
    }
    if (authStore.isAuthenticated) {
      await enrich();
    }
  }

  function startRefreshInterval() {
    stopRefreshInterval();
    refreshTimer = setInterval(() => {
      if (!paused) {
        refreshAndEnrich();
      }
    }, REFRESH_INTERVAL_MS);

    visibilityHandler = () => {
      paused = document.hidden;
    };
    document.addEventListener('visibilitychange', visibilityHandler);
  }

  function stopRefreshInterval() {
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
    }
  }

  // Watch eventId for share-link page where ID arrives asynchronously
  const hasEventId = computed(() => !!eventId.value);
  watch(hasEventId, (hasId, hadId) => {
    if (hasId && !hadId && authStore.isAuthenticated) {
      enrich();
    }
  });

  onMounted(() => {
    if (authStore.isAuthenticated) {
      enrich();
    } else if (authStore.isLoading) {
      const stopWatch = watch(
        () => authStore.isLoading,
        (loading) => {
          if (!loading) {
            stopWatch();
            if (authStore.isAuthenticated) {
              enrich();
            }
          }
        },
      );
    }

    startRefreshInterval();
  });

  onUnmounted(() => {
    stopRefreshInterval();
  });

  return {
    viewerRole,
    commitment,
    purchase,
    registration,
    claimedAttendance,
    isEnriching,
    enrich,
  };
}
